import { Fragment, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InfoIcon, SlidersHorizontalIcon, XIcon } from '@phosphor-icons/react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Tooltip as InfoTooltip } from '../components/Tooltip';
import {
  assignCategoryColors,
  formatCompactCurrency,
  formatCurrency,
  splitCategoryEmoji,
  stripCategoryEmoji,
} from '@budget/core';
import type {
  ClaimAge,
  RetirementAccount,
  RetirementProjection,
  RetirementYear,
  SocialSecurityData,
} from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { getPersonName } from '../config/personNames';
import { type Scope } from '../components/ScopeToggle';
import { useScope } from '../hooks/useScope';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { BreakdownLegend } from '../components/BreakdownLegend';
import { DonutCenterEmoji } from '../components/DonutCenterEmoji';
import { CardHeader } from '../components/CardHeader';
import {
  StatGrid,
  StatItem,
  StatSubText,
  ValueUnit,
} from '../components/StatBlock';
import { Select } from '../components/Select';
import { useChartReady } from '../hooks/useChartReady';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { RetirementGrowthChart } from '../components/RetirementGrowthChart';
import clsx from 'clsx';

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: 'var(--berg-text)' };

function fmt0(n: number) {
  return formatCurrency(n, 'USD', 'en-US', 0);
}
function pct(n: number) {
  // Round to 2 decimals, then drop trailing zeros via parseFloat so "4.00%"
  // reads as "4%" while "4.50%" still keeps meaningful precision as "4.5%".
  return `${parseFloat((n * 100).toFixed(2))}%`;
}

// Selectable scenario rates for the projection-tab dropdowns. Whole-percent
// steps cover the realistic span without overwhelming the menu; the sheet's
// own default (typically 8% / 4%) lands inside the standard range.
const RETURN_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1); // 1..15
const WITHDRAWAL_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1); // 1..10

// Pulled into a helper because we sum-by-year for the household stacked
// chart and need to align two projections that may end at different years.
function indexByYear(years: RetirementYear[]): Map<number, RetirementYear> {
  const m = new Map<number, RetirementYear>();
  for (const y of years) m.set(y.year, y);
  return m;
}

// Dynamic subtitle for the projected-growth chart. Drives the chart's
// header copy from the same numbers the hero displays so the two stay
// visibly in lockstep when the year stepper or scope toggle moves.
function formatGrowthSubtitle(
  dakota: RetirementProjection,
  natalia: RetirementProjection,
  scope: Scope,
  selectedYear: number,
): string {
  const dKey = indexByYear(dakota.years);
  const nKey = indexByYear(natalia.years);
  const allYears = Array.from(new Set([...dKey.keys(), ...nKey.keys()])).sort(
    (a, b) => a - b,
  );
  const firstYear = allYears[0];
  const targetYear = selectedYear;
  const start =
    scope === 'dakota'
      ? dKey.get(firstYear)?.totals.initial ?? 0
      : scope === 'natalia'
        ? nKey.get(firstYear)?.totals.initial ?? 0
        : (dKey.get(firstYear)?.totals.initial ?? 0) +
          (nKey.get(firstYear)?.totals.initial ?? 0);
  const end =
    scope === 'dakota'
      ? dKey.get(targetYear)?.totals.finalAmount ?? 0
      : scope === 'natalia'
        ? nKey.get(targetYear)?.totals.finalAmount ?? 0
        : (dKey.get(targetYear)?.totals.finalAmount ?? 0) +
          (nKey.get(targetYear)?.totals.finalAmount ?? 0);
  const years = Math.max(0, targetYear - firstYear);
  const multiple = start > 0 ? end / start : 0;
  const multipleText = multiple >= 1 ? `${multiple.toFixed(1)}×` : '—';
  return `${multipleText} from ${fmt0(start)} over ${years} years`;
}

// Forward-project each account year-by-year using a user-supplied return /
// withdrawal rate. The sheet already returned a fully-computed projection
// using its baked-in rates (8% / 4%); when the user wants to see a different
// scenario we re-run the math here so the HeroCard, growth chart, AccountMix,
// and year table all reflect the override consistently.
//
// Each account compounds monthly using `returnRate / 12`, with contribution
// and withdrawal evenly split across the 12 months. Mirrors `projectBalance`
// in the Calculator so the two views stay numerically aligned.
function recomputeProjection(
  p: RetirementProjection,
  returnRate: number,
  withdrawalRate: number,
): RetirementProjection {
  const monthly = returnRate / 12;
  // Track each account's running balance across years so year N starts from
  // year N-1's recomputed finalAmount, not the sheet's value.
  const balances = new Map<string, number>();
  for (const a of p.years[0]?.accounts ?? []) balances.set(a.name, a.initial);

  const newYears: RetirementYear[] = p.years.map((year) => {
    const newAccounts: RetirementAccount[] = year.accounts.map((a) => {
      const startBalance = balances.get(a.name) ?? a.initial;
      const monthlyContrib = (a.totalContributionAnnual - a.withdrawals) / 12;
      let balance = startBalance;
      for (let m = 0; m < 12; m++) {
        balance = balance * (1 + monthly) + monthlyContrib;
      }
      balances.set(a.name, balance);
      return {
        ...a,
        initial: startBalance,
        finalAmount: balance,
        averageReturn: returnRate,
        withdrawalRule: balance * withdrawalRate,
      };
    });
    const totalFinal = newAccounts.reduce((s, a) => s + a.finalAmount, 0);
    const totalInitial = newAccounts.reduce((s, a) => s + a.initial, 0);
    const totalContribution = newAccounts.reduce((s, a) => s + a.contributionAnnual, 0);
    const totalMatch = newAccounts.reduce((s, a) => s + a.matchAnnual, 0);
    return {
      ...year,
      accounts: newAccounts.map((a) => ({
        ...a,
        portfolioPercent: totalFinal > 0 ? (a.finalAmount / totalFinal) * 100 : 0,
      })),
      totals: {
        initial: totalInitial,
        contributionAnnual: totalContribution,
        matchAnnual: totalMatch,
        totalContributionAnnual: totalContribution + totalMatch,
        averageReturn: returnRate,
        finalAmount: totalFinal,
        withdrawalRule: totalFinal * withdrawalRate,
      },
    };
  });

  return {
    assumptions: {
      ...p.assumptions,
      averageReturnRate: returnRate,
      withdrawalRule: withdrawalRate,
    },
    years: newYears,
  };
}

/* ===== Contributions vs Gains breakdown =====
   Companion to the Projection hero card. Replicates the AssetBreakdownCard
   pattern from the Assets tab: header + total on the right, a donut, and a
   BreakdownLegend underneath. Two slices — Contributions (today's balance +
   everything you'll add over the window) and Gains (everything compounding
   earns on top). */

function ContributionsBreakdownCard({
  dakota,
  natalia,
  scope,
  selectedYear,
}: {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
  selectedYear: number;
}) {
  const dKey = indexByYear(dakota.years);
  const nKey = indexByYear(natalia.years);
  const allYears = Array.from(new Set([...dKey.keys(), ...nKey.keys()])).sort(
    (a, b) => a - b,
  );
  const firstYear = allYears[0];
  const targetYear = selectedYear;
  const pickInitial = (s: Scope) =>
    s === 'dakota'
      ? dKey.get(firstYear)?.totals.initial ?? 0
      : s === 'natalia'
        ? nKey.get(firstYear)?.totals.initial ?? 0
        : (dKey.get(firstYear)?.totals.initial ?? 0) +
          (nKey.get(firstYear)?.totals.initial ?? 0);
  const pickFinal = (s: Scope) =>
    s === 'dakota'
      ? dKey.get(targetYear)?.totals.finalAmount ?? 0
      : s === 'natalia'
        ? nKey.get(targetYear)?.totals.finalAmount ?? 0
        : (dKey.get(targetYear)?.totals.finalAmount ?? 0) +
          (nKey.get(targetYear)?.totals.finalAmount ?? 0);
  // Sum each year's totalContributionAnnual across the projection window —
  // pairs with the final-minus-initial difference so contributions + gains
  // reconciles exactly to projected growth.
  const sumContributions = (m: Map<number, RetirementYear>) => {
    let acc = 0;
    for (let y = firstYear; y <= targetYear; y++) {
      acc += m.get(y)?.totals.totalContributionAnnual ?? 0;
    }
    return acc;
  };
  const periodContributions =
    scope === 'dakota'
      ? sumContributions(dKey)
      : scope === 'natalia'
        ? sumContributions(nKey)
        : sumContributions(dKey) + sumContributions(nKey);
  const start = pickInitial(scope);
  const end = pickFinal(scope);
  const contributions = start + periodContributions;
  const gains = end - contributions;
  const total = contributions + Math.max(0, gains);

  // Pull from the shared CATEGORY_COLORS map (via assignCategoryColors) so
  // these slices stay consistent with every other category-colored donut /
  // legend across the app, instead of one-off hardcoded brand tokens.
  const colorMap = useMemo(
    () => assignCategoryColors(['Contributions', 'Gains']),
    [],
  );
  const items = [
    {
      key: 'contributions',
      label: 'Contributions',
      value: Math.max(0, contributions),
      color: colorMap.get('Contributions') ?? 'var(--berg-neutral)',
    },
    {
      key: 'gains',
      label: 'Gains',
      value: Math.max(0, gains),
      color: colorMap.get('Gains') ?? 'var(--berg-neutral)',
    },
  ];
  const pieData = items.filter((i) => i.value > 0);
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="card h-full">
      <CardHeader
        title="Breakdown"
        subtitle={`Through ${targetYear}`}
        right={<span className="tabular text-2xl font-semibold">{fmt0(total)}</span>}
      />
      {pieData.length === 0 ? (
        <div className="empty-state py-8">No projection in this scope.</div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  animationDuration={1000}
                  nameKey="label"
                  innerRadius="60%"
                  outerRadius="98%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0.5}
                  stroke="var(--berg-bg-elevated)"
                  strokeWidth={1}
                  onMouseEnter={(_, idx) => setHovered(pieData[idx]?.key ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {pieData.map((b) => (
                    <Cell
                      key={b.key}
                      fill={b.color}
                      fillOpacity={hovered === null || hovered === b.key ? 1 : 0.2}
                      style={{ transition: 'fill-opacity 0.18s ease' }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmt0(typeof v === 'number' ? v : 0)}
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <BreakdownLegend
            items={items}
            total={total}
            formatValue={fmt0}
            hovered={hovered}
            onHoverChange={setHovered}
          />
        </div>
      )}
    </div>
  );
}

/* ===== Hero ===== */

function HeroCard({
  dakota,
  natalia,
  scope,
  selectedYear,
  annualExpensesForScope,
}: {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
  selectedYear: number;
  annualExpensesForScope: number;
}) {
  const dKey = indexByYear(dakota.years);
  const nKey = indexByYear(natalia.years);
  const allYears = Array.from(new Set([...dKey.keys(), ...nKey.keys()])).sort(
    (a, b) => a - b
  );
  const firstYear = allYears[0];
  // The hero now shows the snapshot at whatever year the page-level
  // dropdown picks rather than always pinning to the projection's last
  // year. The "Today" stat tile still uses firstYear since that's an
  // anchor point, not a knob.
  const targetYear = selectedYear;

  const startFor = (s: Scope) => {
    if (s === 'dakota') return dKey.get(firstYear)?.totals.initial ?? 0;
    if (s === 'natalia') return nKey.get(firstYear)?.totals.initial ?? 0;
    return (
      (dKey.get(firstYear)?.totals.initial ?? 0) +
      (nKey.get(firstYear)?.totals.initial ?? 0)
    );
  };
  const endFor = (s: Scope) => {
    if (s === 'dakota') return dKey.get(targetYear)?.totals.finalAmount ?? 0;
    if (s === 'natalia') return nKey.get(targetYear)?.totals.finalAmount ?? 0;
    return (
      (dKey.get(targetYear)?.totals.finalAmount ?? 0) +
      (nKey.get(targetYear)?.totals.finalAmount ?? 0)
    );
  };
  const withdrawalFor = (s: Scope) => {
    if (s === 'dakota') return dKey.get(targetYear)?.totals.withdrawalRule ?? 0;
    if (s === 'natalia') return nKey.get(targetYear)?.totals.withdrawalRule ?? 0;
    return (
      (dKey.get(targetYear)?.totals.withdrawalRule ?? 0) +
      (nKey.get(targetYear)?.totals.withdrawalRule ?? 0)
    );
  };
  // Each person's age at targetYear, derived from their first projection
  // row + year offset. We only show an age when scope is a single person —
  // Dakota and Natalia aren't the same age, so the household view drops it.
  const dFirst = dakota.years[0];
  const nFirst = natalia.years[0];
  const ageAt = (
    base: { year: number; age: number } | undefined,
    year: number
  ): number | null => (base ? base.age + (year - base.year) : null);
  const finalAge =
    scope === 'dakota'
      ? ageAt(dFirst, targetYear)
      : scope === 'natalia'
        ? ageAt(nFirst, targetYear)
        : null;

  const start = startFor(scope);
  const end = endFor(scope);
  const annualWithdrawal = withdrawalFor(scope);
  const monthlyWithdrawal = annualWithdrawal / 12;
  const projectionYears = Math.max(0, targetYear - firstYear);
  const growth = end - start;
  const expensesMultiple =
    annualExpensesForScope > 0 ? end / annualExpensesForScope : 0;

  return (
    <div className="card flex flex-col gap-5 h-full">
      <div className="flex flex-col gap-1">
        <span className="hero-eyebrow">
          Projected balance{finalAge != null ? ` at age ${finalAge}` : ''}
        </span>
        <div className="hero-balance tabular leading-none">{fmt0(end)}</div>
        <div className="mt-2">
          <PersonBreakdown
            dakota={dKey.get(targetYear)?.totals.finalAmount ?? 0}
            natalia={nKey.get(targetYear)?.totals.finalAmount ?? 0}
            digits={0}
          />
        </div>
      </div>

      <StatGrid columns={2} gap={6} className="border-t border-border pt-4">
        <StatItem
          label="Today"
          valueSize="xl"
          value={fmt0(start)}
          sub={<StatSubText unit="current balance" />}
        />
        <StatItem
          label="Total growth"
          valueSize="xl"
          valueTone="pos"
          value={`+${fmt0(growth)}`}
          sub={<StatSubText unit={`over ${projectionYears} years`} />}
        />
        <StatItem
          label={`Safe income (${pct(dakota.assumptions.withdrawalRule)} rule)`}
          valueSize="xl"
          value={fmt0(annualWithdrawal)}
          sub={<StatSubText amount={`≈ ${fmt0(monthlyWithdrawal)}`} unit="/mo" />}
        />
        <StatItem
          label="Expense multiple"
          valueSize="xl"
          value={expensesMultiple > 0 ? `${expensesMultiple.toFixed(1)}×` : '—'}
          sub={
            <StatSubText
              amount={fmt0(annualExpensesForScope)}
              unit="/yr today"
            />
          }
        />
      </StatGrid>
    </div>
  );
}

/* ===== Account mix ===== */

type SnapshotMoment = 'today' | 'end';

// Builds a per-account aggregate at either the first year of the projection
// (today) or the page-level selected year (end). For household scope, sums
// the same account across both people (e.g. both 401Ks combine into a single
// "401K" slice).
function aggregateAccounts(
  dakota: RetirementProjection,
  natalia: RetirementProjection,
  scope: Scope,
  moment: SnapshotMoment,
  selectedYear: number
): { name: string; value: number }[] {
  const pickYear = (p: RetirementProjection): RetirementYear | undefined =>
    moment === 'today' ? p.years[0] : p.years.find((y) => y.year === selectedYear);
  const valueOf = (a: RetirementAccount) =>
    moment === 'today' ? a.initial : a.finalAmount;

  // Always build the union of account names across both people so the legend
  // stays stable across scope changes. Missing entries fall through as $0
  // instead of disappearing, which would shift the page below the chart.
  const householdTotal = new Map<string, number>();
  const seed = (year: RetirementYear | undefined) => {
    if (!year) return;
    for (const a of year.accounts) {
      householdTotal.set(a.name, (householdTotal.get(a.name) ?? 0) + valueOf(a));
    }
  };
  seed(pickYear(dakota));
  seed(pickYear(natalia));

  const totals = new Map<string, number>();
  for (const name of householdTotal.keys()) totals.set(name, 0);
  const accumulate = (year: RetirementYear | undefined) => {
    if (!year) return;
    for (const a of year.accounts) {
      totals.set(a.name, (totals.get(a.name) ?? 0) + valueOf(a));
    }
  };
  if (scope === 'dakota' || scope === 'household') accumulate(pickYear(dakota));
  if (scope === 'natalia' || scope === 'household') accumulate(pickYear(natalia));

  // Sort by the scoped value so the legend stays size-ordered for whichever
  // scope is active. The set of items doesn't change across scopes — the
  // household union seeds every name at 0, so missing entries stay visible
  // (just at the bottom).
  return Array.from(totals.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function AccountMix({
  dakota,
  natalia,
  scope,
  selectedYear,
}: {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
  selectedYear: number;
}) {
  const [moment, setMoment] = useState<SnapshotMoment>('today');
  const data = useMemo(
    () => aggregateAccounts(dakota, natalia, scope, moment, selectedYear),
    [dakota, natalia, scope, moment, selectedYear]
  );
  const colorMap = useMemo(
    () => assignCategoryColors(data.map((d) => d.name)),
    [data]
  );
  const total = data.reduce((s, d) => s + d.value, 0);
  const [hovered, setHoveredRaw] = useState<string | null>(null);
  // Defer hover-dim until the entry animation has run — recharts cancels
  // the swoop-in when child props change, so an over-the-chart cursor
  // at mount time would otherwise freeze the load.
  const ready = useChartReady();
  const setHovered = (next: string | null) => {
    if (ready) setHoveredRaw(next);
  };

  // firstYear is a stable anchor (always the projection start). The "end"
  // year reads from the page-level dropdown so it tracks whatever the user
  // picked there.
  const firstYear = Math.min(
    dakota.years[0]?.year ?? Infinity,
    natalia.years[0]?.year ?? Infinity
  );
  const endYear = selectedYear;
  const subtitleYear = moment === 'today' ? firstYear : endYear;

  return (
    <div className="card">
      <CardHeader
        title="Account mix"
        subtitle={`${moment === 'today' ? 'Current' : 'Projected'} balance by account · ${subtitleYear}`}
        right={
          <div className="scope-toggle" role="tablist">
            <button
              role="tab"
              aria-selected={moment === 'today'}
              onClick={() => setMoment('today')}
              className={clsx('scope-toggle-btn', moment === 'today' && 'scope-toggle-btn-active')}
            >
              Today
            </button>
            <button
              role="tab"
              aria-selected={moment === 'end'}
              onClick={() => setMoment('end')}
              className={clsx('scope-toggle-btn', moment === 'end' && 'scope-toggle-btn-active')}
            >
              At {endYear}
            </button>
          </div>
        }
      />
      {data.length === 0 ? (
        <div className="empty-state py-8">No accounts in this view.</div>
      ) : (
        <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1">
          <div className="relative h-[320px] max-md:h-[220px] self-center">
            <DonutCenterEmoji
              emoji={hovered ? splitCategoryEmoji(hovered).emoji : null}
              size="md"
            />
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  animationDuration={1000}
                  nameKey="name"
                  innerRadius="60%"
                  outerRadius="98%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0.5}
                  stroke="var(--berg-bg-elevated)"
                  strokeWidth={1}
                  onMouseEnter={(_, i) => setHovered(data[i]?.name ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {data.map((d) => (
                    <Cell
                      key={d.name}
                      fill={colorMap.get(d.name) ?? 'var(--berg-neutral)'}
                      fillOpacity={hovered === null || hovered === d.name ? 1 : 0.2}
                      style={{ transition: 'fill-opacity 0.18s ease' }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmt0(typeof v === 'number' ? v : 0)}
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="self-center w-full">
            <BreakdownLegend
              items={data.map((d) => ({
                key: d.name,
                label: stripCategoryEmoji(d.name),
                value: d.value,
                color: colorMap.get(d.name) ?? 'var(--berg-neutral)',
              }))}
              total={total}
              formatValue={fmt0}
              hovered={hovered}
              onHoverChange={setHovered}
              collapseAfter={Infinity}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Calculator ===== */

// Compounds monthly. Returns ending balance after `years` years of saving.
// Treats `monthlyContribution` as deposited at end-of-month.
function projectBalance(
  startBalance: number,
  monthlyContribution: number,
  annualReturnRate: number,
  years: number
): { months: { month: number; balance: number }[]; finalBalance: number } {
  const months: { month: number; balance: number }[] = [];
  const monthlyRate = annualReturnRate / 12;
  let balance = startBalance;
  const totalMonths = Math.max(0, Math.round(years * 12));
  months.push({ month: 0, balance });
  for (let i = 1; i <= totalMonths; i++) {
    balance = balance * (1 + monthlyRate) + monthlyContribution;
    months.push({ month: i, balance });
  }
  return { months, finalBalance: balance };
}

function NumberInput({
  label,
  value,
  onChange,
  prefix,
  suffix,
  step = 1,
  min = 0,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  step?: number;
  min?: number;
  max?: number;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="stat-label">{label}</span>
      <div className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border bg-subtle focus-within:border-border-strong transition-colors">
        {prefix && <span className="text-fg-tertiary tabular">{prefix}</span>}
        <input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => {
            const next = parseFloat(e.target.value);
            const fallback = Number.isFinite(next) ? next : 0;
            const clamped =
              max != null
                ? Math.min(Math.max(fallback, min), max)
                : Math.max(fallback, min);
            onChange(clamped);
          }}
          step={step}
          min={min}
          max={max}
          className="flex-1 min-w-0 bg-transparent outline-none tabular text-sm text-fg"
        />
        {suffix && <span className="text-fg-tertiary tabular">{suffix}</span>}
      </div>
    </label>
  );
}

type CalculatorMode = 'classic' | 'goal';

function Calculator({
  dakota,
  natalia,
  scope,
}: {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
}) {
  // Pre-fill personal data (balance, contribution) from the spreadsheet for
  // the current scope. The rate / age fields use plain canonical defaults so
  // the calculator reads as a simple "what if" tool independent of whichever
  // assumptions happen to be in the sheet today.
  const defaults = useMemo(() => {
    const first = (p: RetirementProjection) => p.years[0];
    const fd = first(dakota);
    const fn = first(natalia);

    const startBalance =
      scope === 'dakota'
        ? fd?.totals.initial ?? 0
        : scope === 'natalia'
          ? fn?.totals.initial ?? 0
          : (fd?.totals.initial ?? 0) + (fn?.totals.initial ?? 0);

    const annualContribution =
      scope === 'dakota'
        ? fd?.totals.totalContributionAnnual ?? 0
        : scope === 'natalia'
          ? fn?.totals.totalContributionAnnual ?? 0
          : (fd?.totals.totalContributionAnnual ?? 0) +
            (fn?.totals.totalContributionAnnual ?? 0);

    const startBalanceRounded = Math.round(startBalance);
    const monthlyContribution = Math.round(annualContribution / 12);
    // Default goal-mode target income: project the classic-mode defaults
    // forward and apply the 4% rule. That way toggling into Goal mode the
    // first time shows roughly the same scenario the user is already viewing.
    const yearsDefault = 65 - 33;
    const forecastFinal = projectBalance(
      startBalanceRounded,
      monthlyContribution,
      0.08,
      yearsDefault,
    ).finalBalance;
    const targetAnnualIncome = Math.max(
      50000,
      Math.round((forecastFinal * 0.04) / 1000) * 1000,
    );

    return {
      startBalance: startBalanceRounded,
      monthlyContribution,
      targetAnnualIncome,
      annualReturnPct: 8,
      withdrawalPct: 4,
      startAge: 33,
      retirementAge: 65,
    };
  }, [dakota, natalia, scope]);

  const [mode, setMode] = useState<CalculatorMode>('classic');
  const [startBalance, setStartBalance] = useState(defaults.startBalance);
  const [monthlyContribution, setMonthlyContribution] = useState(
    defaults.monthlyContribution
  );
  const [targetAnnualIncome, setTargetAnnualIncome] = useState(
    defaults.targetAnnualIncome
  );
  const [annualReturnPct, setAnnualReturnPct] = useState(defaults.annualReturnPct);
  const [withdrawalPct, setWithdrawalPct] = useState(defaults.withdrawalPct);
  const [startAge, setStartAge] = useState(defaults.startAge);
  const [retirementAge, setRetirementAge] = useState(defaults.retirementAge);

  // Fields the user has manually edited. On scope change we only refresh
  // fields that are still showing their derived default — a value the user
  // typed in stays put across scope switches.
  type FieldKey =
    | 'startBalance'
    | 'monthlyContribution'
    | 'targetAnnualIncome'
    | 'annualReturnPct'
    | 'withdrawalPct'
    | 'startAge'
    | 'retirementAge';
  const [touched, setTouched] = useState<Set<FieldKey>>(() => new Set());
  const bind =
    <F extends FieldKey>(field: F, setter: (v: number) => void) =>
    (v: number) => {
      setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
      setter(v);
    };

  // Only refresh untouched fields when the scope changes; whatever the user
  // typed survives the switch.
  const [lastScope, setLastScope] = useState(scope);
  if (lastScope !== scope) {
    if (!touched.has('startBalance')) setStartBalance(defaults.startBalance);
    if (!touched.has('monthlyContribution'))
      setMonthlyContribution(defaults.monthlyContribution);
    if (!touched.has('targetAnnualIncome'))
      setTargetAnnualIncome(defaults.targetAnnualIncome);
    if (!touched.has('annualReturnPct'))
      setAnnualReturnPct(defaults.annualReturnPct);
    if (!touched.has('withdrawalPct')) setWithdrawalPct(defaults.withdrawalPct);
    if (!touched.has('startAge')) setStartAge(defaults.startAge);
    if (!touched.has('retirementAge')) setRetirementAge(defaults.retirementAge);
    setLastScope(scope);
  }

  // Scope-aware accent for the chart and the headline projected-balance
  // tile. Matches the growth chart's per-person hues so the calculator
  // visually belongs to whichever person is currently scoped.
  const scopeColor =
    scope === 'dakota'
      ? 'var(--berg-dakota)'
      : scope === 'natalia'
        ? 'var(--berg-natalia)'
        : 'var(--berg-green)';

  const yearsToRetirement = Math.max(0, retirementAge - startAge);

  // Goal-mode math: back into the monthly contribution needed to reach the
  // nest egg that supports the target income at the chosen withdrawal rate.
  // Future-value annuity formula:
  //   FV = P*(1+r)^n + PMT * ((1+r)^n - 1) / r
  // Solve for PMT, clamp at 0 (already on track requires no new savings).
  const monthlyRate = annualReturnPct / 100 / 12;
  const totalMonths = Math.max(0, Math.round(yearsToRetirement * 12));
  const requiredNestEgg =
    withdrawalPct > 0 ? targetAnnualIncome / (withdrawalPct / 100) : 0;
  const compoundedStart =
    totalMonths > 0
      ? startBalance * Math.pow(1 + monthlyRate, totalMonths)
      : startBalance;
  const annuityFactor =
    monthlyRate === 0
      ? totalMonths
      : (Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate;
  const requiredMonthly =
    annuityFactor === 0
      ? 0
      : Math.max(0, (requiredNestEgg - compoundedStart) / annuityFactor);

  const effectiveMonthlyContribution =
    mode === 'goal' ? requiredMonthly : monthlyContribution;

  const projection = useMemo(
    () =>
      projectBalance(
        startBalance,
        effectiveMonthlyContribution,
        annualReturnPct / 100,
        yearsToRetirement
      ),
    [startBalance, effectiveMonthlyContribution, annualReturnPct, yearsToRetirement]
  );
  const totalContributed = effectiveMonthlyContribution * 12 * yearsToRetirement;
  const earnings = projection.finalBalance - startBalance - totalContributed;
  const annualWithdrawal = projection.finalBalance * (withdrawalPct / 100);

  // Down-sample the per-month series to one point per year for the chart.
  const chartData = useMemo(() => {
    const points: { age: number; balance: number }[] = [];
    for (let i = 0; i <= yearsToRetirement; i++) {
      const monthIdx = i * 12;
      const balance = projection.months[Math.min(monthIdx, projection.months.length - 1)]?.balance ?? 0;
      points.push({ age: startAge + i, balance });
    }
    return points;
  }, [projection.months, startAge, yearsToRetirement]);

  const reset = () => {
    setStartBalance(defaults.startBalance);
    setMonthlyContribution(defaults.monthlyContribution);
    setTargetAnnualIncome(defaults.targetAnnualIncome);
    setAnnualReturnPct(defaults.annualReturnPct);
    setWithdrawalPct(defaults.withdrawalPct);
    setStartAge(defaults.startAge);
    setRetirementAge(defaults.retirementAge);
    setTouched(new Set());
  };

  return (
    <div
      className="card flex flex-col gap-5"
      style={{
        background:
          'color-mix(in srgb, var(--berg-bg-elevated) 70%, transparent)',
        backdropFilter: 'saturate(160%) blur(12px)',
        WebkitBackdropFilter: 'saturate(160%) blur(12px)',
      }}
    >
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-xl sm:text-2xl min-w-0 truncate">
            Calculator
          </h3>
          <div className="flex items-center gap-3 flex-none shrink-0">
            <div className="scope-toggle" role="tablist">
              <button
                role="tab"
                aria-selected={mode === 'classic'}
                onClick={() => setMode('classic')}
                className={clsx(
                  'scope-toggle-btn',
                  mode === 'classic' && 'scope-toggle-btn-active'
                )}
              >
                Classic
              </button>
              <button
                role="tab"
                aria-selected={mode === 'goal'}
                onClick={() => setMode('goal')}
                className={clsx(
                  'scope-toggle-btn',
                  mode === 'goal' && 'scope-toggle-btn-active'
                )}
              >
                Goal
              </button>
            </div>
            <button
              type="button"
              onClick={reset}
              className="btn btn-secondary btn-sm"
              title="Reset to spreadsheet values"
            >
              Reset
            </button>
          </div>
        </div>
        <p className="text-sm text-fg-tertiary mt-0.5">
          {mode === 'classic'
            ? 'Set your contribution — see the balance you’ll land on'
            : 'Set your target income — see what you need to save'}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 max-md:grid-cols-2 -mt-3">
        <NumberInput
          label="Starting balance"
          value={startBalance}
          onChange={bind('startBalance', setStartBalance)}
          prefix="$"
          step={1000}
        />
        {mode === 'classic' ? (
          <NumberInput
            label="Contribution"
            value={monthlyContribution}
            onChange={bind('monthlyContribution', setMonthlyContribution)}
            prefix="$"
            suffix="/mo"
            step={50}
          />
        ) : (
          <NumberInput
            label="Target income"
            value={targetAnnualIncome}
            onChange={bind('targetAnnualIncome', setTargetAnnualIncome)}
            prefix="$"
            suffix="/yr"
            step={1000}
          />
        )}
        <NumberInput
          label="Annual return"
          value={annualReturnPct}
          onChange={bind('annualReturnPct', setAnnualReturnPct)}
          suffix="%"
          step={1}
        />
        <NumberInput
          label="Current age"
          value={startAge}
          onChange={bind('startAge', setStartAge)}
          step={1}
          min={0}
        />
        <NumberInput
          label="Retirement age"
          value={retirementAge}
          onChange={bind('retirementAge', setRetirementAge)}
          step={1}
          min={0}
        />
        <NumberInput
          label="Withdrawal rate"
          value={withdrawalPct}
          onChange={bind('withdrawalPct', setWithdrawalPct)}
          suffix="%"
          step={1}
        />
      </div>

      <StatGrid columns={3} className="border-t border-border pt-4 max-sm:grid-cols-1!">
        {mode === 'classic' ? (
          <>
            <StatItem
              label={`At age ${retirementAge}`}
              valueSize="xl"
              value={
                <span style={{ color: scopeColor }}>
                  {fmt0(projection.finalBalance)}
                </span>
              }
              sub={<StatSubText unit={`after ${yearsToRetirement} years`} />}
            />
            <StatItem
              label="Contributions vs growth"
              valueSize="xl"
              value={fmt0(totalContributed)}
              sub={
                <StatSubText
                  amount={
                    <span className="delta-pos">
                      +{fmt0(Math.max(0, earnings))}
                    </span>
                  }
                  unit="from compounding"
                />
              }
            />
            <StatItem
              label={`Annual income at ${Number(withdrawalPct.toFixed(2))}%`}
              valueSize="xl"
              value={
                <>
                  {fmt0(annualWithdrawal)}
                  <ValueUnit>/yr</ValueUnit>
                </>
              }
              sub={
                <StatSubText
                  amount={`≈ ${fmt0(annualWithdrawal / 12)}`}
                  unit="/mo"
                />
              }
            />
          </>
        ) : (
          <>
            <StatItem
              label={`Need by age ${retirementAge}`}
              valueSize="xl"
              value={
                <span style={{ color: scopeColor }}>
                  {fmt0(requiredNestEgg)}
                </span>
              }
              sub={
                <StatSubText
                  unit={`funds ${fmt0(targetAnnualIncome)}/yr at ${Number(withdrawalPct.toFixed(2))}%`}
                />
              }
            />
            <StatItem
              label="Required contribution"
              valueSize="xl"
              value={
                <>
                  {fmt0(requiredMonthly)}
                  <ValueUnit>/mo</ValueUnit>
                </>
              }
              sub={
                <StatSubText
                  amount={`≈ ${fmt0(requiredMonthly * 12)}`}
                  unit="/yr"
                />
              }
            />
            <StatItem
              label="Contributions vs growth"
              valueSize="xl"
              value={fmt0(totalContributed)}
              sub={
                <StatSubText
                  amount={
                    <span className="delta-pos">
                      +{fmt0(Math.max(0, earnings))}
                    </span>
                  }
                  unit="from compounding"
                />
              }
            />
          </>
        )}
      </StatGrid>

      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="grad-calc" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={scopeColor} stopOpacity={0.5} />
                <stop offset="100%" stopColor={scopeColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--berg-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="age"
              tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
              axisLine={false}
              tickLine={false}
              minTickGap={20}
              tickFormatter={(v: number) => `age ${v}`}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
              tickFormatter={(v: number) => formatCompactCurrency(v)}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip
              formatter={(v) => fmt0(typeof v === 'number' ? v : 0)}
              contentStyle={TOOLTIP_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              cursor={{ stroke: 'var(--berg-border-strong)', strokeWidth: 1 }}
              labelFormatter={(v) => `Age ${v}`}
            />
            <Area
              type="monotone"
              dataKey="balance"
              name="Balance"
              stroke={scopeColor}
              strokeWidth={2}
              fill="url(#grad-calc)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ===== Year-by-year table ===== */

const CLAIM_AGE_OPTIONS: ClaimAge[] = [62, 67, 70];

function YearTable({
  dakota,
  natalia,
  scope,
  selectedYear,
  onSelectYear,
}: {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
  // Rows past this year render at reduced opacity so the table visually
  // de-emphasizes everything beyond the user's selected target year.
  selectedYear: number;
  onSelectYear: (year: number) => void;
}) {
  const dKey = indexByYear(dakota.years);
  const nKey = indexByYear(natalia.years);
  const allYears = Array.from(new Set([...dKey.keys(), ...nKey.keys()])).sort(
    (a, b) => a - b
  );

  // Compute each person's age from their first projection row + offset
  // instead of reading the per-year row's age — projections end at different
  // points (Natalia's is shorter than Dakota's), and falling back to the
  // other person's age would mis-label rows.
  const dFirst = dakota.years[0];
  const nFirst = natalia.years[0];
  const ageOf = (
    base: { year: number; age: number } | undefined,
    year: number
  ): number | null => (base ? base.age + (year - base.year) : null);

  const rows = allYears.map((year) => {
    const d = dKey.get(year);
    const n = nKey.get(year);
    const pickInitial =
      scope === 'dakota'
        ? d?.totals.initial ?? 0
        : scope === 'natalia'
          ? n?.totals.initial ?? 0
          : (d?.totals.initial ?? 0) + (n?.totals.initial ?? 0);
    const pickContrib =
      scope === 'dakota'
        ? d?.totals.totalContributionAnnual ?? 0
        : scope === 'natalia'
          ? n?.totals.totalContributionAnnual ?? 0
          : (d?.totals.totalContributionAnnual ?? 0) +
            (n?.totals.totalContributionAnnual ?? 0);
    const pickFinal =
      scope === 'dakota'
        ? d?.totals.finalAmount ?? 0
        : scope === 'natalia'
          ? n?.totals.finalAmount ?? 0
          : (d?.totals.finalAmount ?? 0) + (n?.totals.finalAmount ?? 0);
    // Dollar return for the year = end - start - contributions. The
    // RetirementYear.totals.averageReturn field is the rate (e.g. 0.08 for
    // 8%), not a dollar amount — surfacing it raw through fmt0 rounded the
    // fractional rate to "+$0", making the column look broken.
    const pickReturn = pickFinal - pickInitial - pickContrib;
    const pickWithdrawal =
      scope === 'dakota'
        ? d?.totals.withdrawalRule ?? 0
        : scope === 'natalia'
          ? n?.totals.withdrawalRule ?? 0
          : (d?.totals.withdrawalRule ?? 0) + (n?.totals.withdrawalRule ?? 0);
    const dakotaAge = ageOf(dFirst, year);
    const nataliaAge = ageOf(nFirst, year);
    return {
      year,
      dakotaAge,
      nataliaAge,
      initial: pickInitial,
      contribution: pickContrib,
      averageReturn: pickReturn,
      finalAmount: pickFinal,
      withdrawalRule: pickWithdrawal,
    };
  });

  // Surface the 59½ "penalty-free withdrawal" milestone with a small info
  // Per-person indexes — each person hits 59½ in their own year, so the
  // table gets two distinct color-coded divider rows (Dakota cyan, Natalia
  // pink). `findIndex` returns -1 when that person never reaches 59 inside
  // the projection window, in which case their divider just doesn't render.
  const dakotaMilestoneIndex = rows.findIndex(
    (r) => r.dakotaAge != null && r.dakotaAge >= 59,
  );
  const nataliaMilestoneIndex = rows.findIndex(
    (r) => r.nataliaAge != null && r.nataliaAge >= 59,
  );

  // Person-colored divider that drops in after the year someone turns 59 —
  // marks the 59½ threshold where their 401(k), Roth 401(k), and Roth IRA
  // withdrawals become penalty-free.
  function MilestoneRow({ person }: { person: 'dakota' | 'natalia' }) {
    const borderClass =
      person === 'dakota' ? 'border-dakota' : 'border-natalia';
    const textClass = person === 'dakota' ? 'text-dakota' : 'text-natalia';
    const headline = `${getPersonName(person)} hits 59½`;
    const detail =
      'penalty-free 401(k), Roth 401(k), & Roth IRA withdrawals begin';
    return (
      <div
        className={clsx(
          'flex items-center gap-3 px-2 py-3 -mx-px border-l border-r max-md:px-4',
          borderClass,
        )}
        style={{
          background:
            'color-mix(in srgb, var(--berg-bg) 50%, var(--berg-bg-elevated))',
        }}
      >
        <span
          aria-hidden
          className={clsx('flex-1 border-t border-dashed', borderClass)}
        />
        <span
          className={clsx(
            'text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-center inline-flex items-center gap-1.5',
            textClass,
          )}
        >
          {/* Desktop: headline + dash + full detail inline. */}
          <span className="max-md:hidden">
            {headline} — {detail}
          </span>
          {/* Mobile: headline + info icon → tooltip with the detail. */}
          <span className="hidden max-md:inline-flex items-center gap-1.5">
            {headline}
            <InfoTooltip wide content={detail}>
              <InfoIcon
                size={12}
                weight="regular"
                className={clsx('cursor-help', textClass)}
                tabIndex={0}
                aria-label="About 59½ penalty-free withdrawals"
              />
            </InfoTooltip>
          </span>
        </span>
        <span
          aria-hidden
          className={clsx('flex-1 border-t border-dashed', borderClass)}
        />
      </div>
    );
  }


  const ageDisplay = (r: (typeof rows)[number]): ReactNode => {
    const fmtAge = (a: number | null) => (a == null ? '—' : String(a));
    if (scope === 'dakota') {
      return (
        <>
          <span className="text-dakota">{fmtAge(r.dakotaAge)}</span>
          <span className="text-fg-tertiary"> / </span>
          <span className="text-natalia opacity-40">{fmtAge(r.nataliaAge)}</span>
        </>
      );
    }
    if (scope === 'natalia') {
      return (
        <>
          <span className="text-dakota opacity-40">{fmtAge(r.dakotaAge)}</span>
          <span className="text-fg-tertiary"> / </span>
          <span className="text-natalia">{fmtAge(r.nataliaAge)}</span>
        </>
      );
    }
    // Household: show both ages so the row reads as a snapshot of where
    // Dakota and Natalia each are in their lives that year. When both ages
    // match (which they often will for couples close in age), collapse to a
    // single value but keep it neutral since it represents both people.
    if (r.dakotaAge === r.nataliaAge && r.dakotaAge != null) {
      return fmtAge(r.dakotaAge);
    }
    return (
      <>
        <span className="text-dakota">{fmtAge(r.dakotaAge)}</span>
        <span className="text-fg-tertiary"> / </span>
        <span className="text-natalia">{fmtAge(r.nataliaAge)}</span>
      </>
    );
  };

  return (
    // overflow-visible lets the milestone row paint its green L/R borders
    // over the card's 1px gray border so the divider lines up flush with
    // the card edge instead of being indented inside it.
    <div className="card-flush overflow-visible">
      <CardHeader
        flush
        title="Year by year"
        subtitle={`${rows.length} years on the projection`}
      />
      <div className="border-t border-border overflow-x-visible max-md:hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="px-5 py-3 text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                Year
              </th>
              <th className="px-5 py-3 text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                Age
              </th>
              <th className="px-5 py-3 text-right text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                Start
              </th>
              <th className="px-5 py-3 text-right text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                Contrib
              </th>
              <th className="px-5 py-3 text-right text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                Return
              </th>
              <th className="px-5 py-3 text-right text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                Final
              </th>
              <th className="px-5 py-3 text-right text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary">
                {Math.round(dakota.assumptions.withdrawalRule * 100)}% income
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <Fragment key={r.year}>
              <tr
                onClick={() => onSelectYear(r.year)}
                className={clsx(
                  'border-t border-border hover:bg-overlay transition-colors cursor-pointer',
                  r.year === selectedYear && 'bg-overlay',
                  r.year > selectedYear && 'opacity-40',
                )}
              >
                <td className="px-5 py-2.5 font-semibold tabular">{r.year}</td>
                <td className="px-5 py-2.5 tabular text-fg-secondary">
                  {ageDisplay(r)}
                </td>
                <td className="px-5 py-2.5 text-right tabular text-fg-secondary">
                  {fmt0(r.initial)}
                </td>
                <td className="px-5 py-2.5 text-right tabular text-fg-secondary">
                  {fmt0(r.contribution)}
                </td>
                <td className="px-5 py-2.5 text-right tabular delta-pos">
                  {r.averageReturn > 0 ? `+${fmt0(r.averageReturn)}` : '—'}
                </td>
                <td className="px-5 py-2.5 text-right tabular font-semibold">
                  {fmt0(r.finalAmount)}
                </td>
                <td className="px-5 py-2.5 text-right tabular text-fg-secondary">
                  {fmt0(r.withdrawalRule)}<span className="text-fg-tertiary">/yr</span>
                </td>
              </tr>
              {i === dakotaMilestoneIndex && scope !== 'natalia' && (
                <tr>
                  <td colSpan={7} className="p-0">
                    <MilestoneRow person="dakota" />
                  </td>
                </tr>
              )}
              {i === nataliaMilestoneIndex && scope !== 'dakota' && (
                <tr>
                  <td colSpan={7} className="p-0">
                    <MilestoneRow person="natalia" />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hidden max-md:block border-t border-border">
        {rows.map((r, i) => (
          <Fragment key={r.year}>
          <div
            onClick={() => onSelectYear(r.year)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectYear(r.year);
              }
            }}
            className={clsx(
              'border-t border-border first:border-t-0 px-4 py-3 flex flex-col gap-2.5 cursor-pointer transition-colors',
              r.year === selectedYear && 'bg-overlay',
            )}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-base font-semibold tabular">{r.year}</span>
              <span className="text-base font-semibold tabular shrink-0">
                {fmt0(r.finalAmount)}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="stat-label">Start</div>
                <div className="tabular text-sm text-fg-secondary mt-0.5">
                  {fmt0(r.initial)}
                </div>
              </div>
              <div>
                <div className="stat-label">Contrib</div>
                <div className="tabular text-sm text-fg-secondary mt-0.5">
                  {fmt0(r.contribution)}
                </div>
              </div>
              <div>
                <div className="stat-label">Return</div>
                <div className="tabular text-sm delta-pos mt-0.5">
                  {r.averageReturn > 0 ? `+${fmt0(r.averageReturn)}` : '—'}
                </div>
              </div>
            </div>
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-fg-tertiary">
                age <span className="tabular">{ageDisplay(r)}</span>
              </span>
              <span className="text-fg-tertiary">
                {Math.round(dakota.assumptions.withdrawalRule * 100)}% income{' '}
                <span className="tabular text-fg-secondary">
                  {fmt0(r.withdrawalRule)}
                </span>
                /yr
              </span>
            </div>
          </div>
          {i === dakotaMilestoneIndex && scope !== 'natalia' && (
            <MilestoneRow person="dakota" />
          )}
          {i === nataliaMilestoneIndex && scope !== 'dakota' && (
            <MilestoneRow person="natalia" />
          )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/* ===== Social Security tab ===== */

// Year that a person reaches `claimAge`, given their first projection row.
// Returns null if the projection has no anchor for that person, or 0 when the
// person is already older than the claim age (already eligible).
function startYearFor(
  base: { year: number; age: number } | undefined,
  claimAge: ClaimAge
): number | null {
  if (!base) return null;
  return base.year + Math.max(0, claimAge - base.age);
}

// Per-card accent for the Social Security scenario cards. Red (early
// claim, smallest payout) → orange (FRA) → yellow (max delayed credit)
// signals that taking SS earlier locks in a permanent reduction.
const CLAIM_AGE_COLOR: Record<ClaimAge, string> = {
  62: 'var(--berg-red)',
  67: 'var(--berg-orange)',
  70: 'var(--berg-yellow)',
};

function ClaimAgeCard({
  claimAge,
  scope,
  socialSecurity,
  dakotaFirst,
  nataliaFirst,
  lifespan,
}: {
  claimAge: ClaimAge;
  scope: Scope;
  socialSecurity: SocialSecurityData;
  dakotaFirst: { year: number; age: number } | undefined;
  nataliaFirst: { year: number; age: number } | undefined;
  lifespan: number;
}) {
  const monthlyTri = socialSecurity.monthly[claimAge];
  const yearlyTri = socialSecurity.yearly[claimAge];
  const monthly = monthlyTri[scope];
  const yearly = yearlyTri[scope];

  // Years from claim age to expected lifespan. Floors at 0 in case the
  // slider lands at or below the claim age (shouldn't happen given the
  // 75–100 range, but keeps the math safe).
  const yearsCollecting = Math.max(0, lifespan - claimAge);
  const lifetime = yearly * yearsCollecting;
  const lifetimeDakota = yearlyTri.dakota * yearsCollecting;
  const lifetimeNatalia = yearlyTri.natalia * yearsCollecting;

  const dStart = startYearFor(dakotaFirst, claimAge);
  const nStart = startYearFor(nataliaFirst, claimAge);

  // First-eligible display. Always shows both years in their identity
  // colors; per-person scopes dim the non-scoped person to 40% — same focus
  // pattern PersonBreakdown applies to the monthly/annual amounts.
  const dakotaDim = scope === 'natalia';
  const nataliaDim = scope === 'dakota';
  const startsYearDisplay: ReactNode =
    dStart == null && nStart == null ? (
      '—'
    ) : (
      <>
        <span
          className={clsx(
            'text-dakota transition-opacity duration-150',
            dakotaDim && 'opacity-40',
          )}
        >
          {dStart ?? '—'}
        </span>
        <span className="text-fg-tertiary"> / </span>
        <span
          className={clsx(
            'text-natalia transition-opacity duration-150',
            nataliaDim && 'opacity-40',
          )}
        >
          {nStart ?? '—'}
        </span>
      </>
    );

  const accent = CLAIM_AGE_COLOR[claimAge];

  return (
    <div className="card flex flex-col gap-5">
      {/* TIMING ROW: the claim decision itself. Claim age on the left,
          dial in the middle showing where this card sits on the 55–75
          scale (so the three cards' dials read as one timeline), and
          the first-eligible year on the right. Everything in this row
          is about WHEN — money lives in the row below.

          On mobile, claim age and the dial sit side-by-side (via the
          inner flex wrapper) and the first-eligible year stacks below
          them. On desktop the inner wrapper switches to `display:
          contents` so its children flatten into the outer flex row —
          claim age, dial, first eligible all share one horizontal line. */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:gap-8">
        <div className="flex items-end gap-4 md:contents">
          <div className="flex-none">
            <span className="hero-eyebrow">Claim at</span>
            <div className="mt-1 flex items-baseline gap-2">
              <span
                className="text-4xl md:text-5xl font-semibold tabular leading-none"
                style={{ color: accent }}
              >
                {claimAge}
              </span>
              <span className="text-fg-tertiary text-base font-medium">
                years old
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <ProgressBar
              value={claimAge}
              niceMin={55}
              niceMax={75}
              color={accent}
              formatLabel={(n) => `${Math.round(n)}`}
            />
          </div>
        </div>
        <div className="md:flex-none md:text-right">
          <span className="hero-eyebrow">First eligible</span>
          <div className="mt-1 text-2xl md:text-3xl font-semibold tabular leading-none">
            {startsYearDisplay}
          </div>
        </div>
      </div>

      {/* MONEY ROW: two symmetric "benefit" tiles framing the same
          income stream from different angles. Monthly shows the
          recurring income (with /yr as a subline); Lifetime shows the
          cumulative total at the user's chosen lifespan (with the
          years-collected range as a subline). Both share the same
          eyebrow → big number → caption structure so they read as
          parallel views, not separate stats. Lifetime borrows the
          claim-age accent so it stands out as the headline comparison
          number across cards. The per-person split sits directly under
          its parent amount when in household scope. */}
      <div className="border-t border-border pt-5 flex flex-col gap-5 md:flex-row md:gap-0 md:items-start">
        <div className="flex flex-col gap-3 md:flex-1 md:pr-8">
          <div>
            <span className="hero-eyebrow">Monthly benefit</span>
            <div className="mt-1 flex flex-col gap-1">
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl md:text-4xl font-semibold tabular leading-none">
                  {fmt0(monthly)}
                </span>
                <span className="text-fg-tertiary text-sm font-medium">
                  /mo
                </span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm text-fg-secondary tabular">
                  {fmt0(yearly)}
                </span>
                <span className="text-xs text-fg-tertiary font-medium">
                  /yr
                </span>
              </div>
            </div>
          </div>
          <PersonBreakdown
            dakota={monthlyTri.dakota}
            natalia={monthlyTri.natalia}
            digits={0}
          />
        </div>
        <div className="flex flex-col gap-3 md:flex-1 md:pl-8 md:border-l md:border-border max-md:border-t max-md:border-border max-md:pt-5">
          <div>
            <span className="hero-eyebrow">Lifetime benefit</span>
            <div className="mt-1 flex flex-col gap-1">
              <span
                className="text-3xl md:text-4xl font-semibold tabular leading-none"
                style={{ color: accent }}
              >
                {fmt0(lifetime)}
              </span>
              <span className="text-fg-tertiary text-xs font-medium">
                Over {yearsCollecting} yrs · age {claimAge}→{lifespan}
              </span>
            </div>
          </div>
          <PersonBreakdown
            dakota={lifetimeDakota}
            natalia={lifetimeNatalia}
            digits={0}
          />
        </div>
      </div>
    </div>
  );
}

function ProgressBar({
  value,
  niceMin = 0,
  niceMax,
  color,
  formatLabel = formatCompactCurrency,
}: {
  value: number;
  // Lower bound of the dial scale. Defaults to 0 for dollar-style dials;
  // age dials lift this so the lowest labeled tick isn't $0.
  niceMin?: number;
  // Upper bound of the dial scale. The caller picks a "nice" round number
  // so the major-tick labels come out clean (e.g. 75 for an age dial).
  niceMax: number;
  color: string;
  formatLabel?: (n: number) => string;
}) {
  // Dial-style scale: a row of hash marks across the full width, with major
  // ticks every 25% labeled, and a colored needle marking the current
  // value. Both ends fade to transparent so the bar reads as a slice of a
  // longer scale rather than a clipped rectangle. The visible window
  // covers ~22–82% — paired with the headroom the caller bakes into
  // niceMax/niceMin, this keeps the needle well inside while preserving a
  // soft fade on both ends.
  const fadeMask =
    'linear-gradient(to right, transparent 0%, black 22%, black 82%, transparent 100%)';
  const range = Math.max(1, niceMax - niceMin);
  const valuePct = Math.min(
    100,
    Math.max(0, ((value - niceMin) / range) * 100),
  );
  const majorCount = 4; // 5 labeled ticks: 0, 25%, 50%, 75%, 100%
  const minorPerMajor = 5; // 4 minor hashes between each pair of majors
  const totalTicks = majorCount * minorPerMajor + 1;

  return (
    <div
      className="relative w-full select-none"
      style={{ maskImage: fadeMask, WebkitMaskImage: fadeMask }}
    >
      {/* Hash marks */}
      <div className="relative flex items-end justify-between h-4">
        {Array.from({ length: totalTicks }, (_, i) => {
          const isMajor = i % minorPerMajor === 0;
          return (
            <span
              key={i}
              className={clsx(
                'block w-px bg-fg-tertiary',
                isMajor ? 'h-3.5' : 'h-2',
              )}
            />
          );
        })}
        {/* Needle marking the current value */}
        <span
          aria-hidden
          className="absolute top-0 h-4 w-0.5 rounded-full"
          style={{
            left: `${valuePct}%`,
            transform: 'translateX(-50%)',
            background: color,
            boxShadow: `0 0 6px ${color}`,
          }}
        />
      </div>
      {/* Numbers under major ticks — absolutely positioned so each label's
          center sits exactly at the matching tick's percentage. A flex
          justify-between layout drifts left of the ticks as label widths
          accumulate, so we anchor each label to its exact tick position
          instead. */}
      <div className="mt-1 relative h-3 text-[0.625rem] tabular text-fg-tertiary">
        {Array.from({ length: majorCount + 1 }, (_, i) => {
          const tickValue = niceMin + (range / majorCount) * i;
          const pct = (i / majorCount) * 100;
          return (
            <span
              key={i}
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${pct}%` }}
            >
              {formatLabel(tickValue)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

const LIFESPAN_MIN = 75;
const LIFESPAN_MAX = 100;
const LIFESPAN_DEFAULT = 90;

function LifespanControl({
  value,
  onChange,
  scope,
}: {
  value: number;
  onChange: (n: number) => void;
  scope: Scope;
}) {
  // Same pct math the hash marks and labels use, so the floating
  // readout horizontally tracks the handle exactly.
  const valuePct =
    ((value - LIFESPAN_MIN) / (LIFESPAN_MAX - LIFESPAN_MIN)) * 100;

  // Tooltip stays centered on the handle for most of the range. Only
  // when it would visibly clip the slider's edge — within the outermost
  // ~15% on either side, roughly the first/last few ticks — does the
  // translateX glide from -50% toward 0% (left edge anchor) or -100%
  // (right edge anchor). On a typical phone the tooltip is ~12% of the
  // slider's inner width, so 15% is the smallest buffer that keeps the
  // tooltip on-screen at the extremes without shifting at "normal"
  // positions.
  //
  // tooltipEdgePx adds a small pixel-level outward shift that absorbs
  // the 12px thumb-radius inset of the tooltip's parent — at the
  // extremes the tooltip's edge lines up with the surrounding page
  // container (where the claim-age cards reach), not with the inset
  // tick area.
  const TOOLTIP_EDGE_BUFFER = 15;
  const TOOLTIP_PARENT_INSET_PX = 12;
  let tooltipTranslatePct = -50;
  let tooltipEdgePx = 0;
  if (valuePct < TOOLTIP_EDGE_BUFFER) {
    const t = valuePct / TOOLTIP_EDGE_BUFFER;
    tooltipTranslatePct = -50 * t;
    tooltipEdgePx = -TOOLTIP_PARENT_INSET_PX * (1 - t);
  } else if (valuePct > 100 - TOOLTIP_EDGE_BUFFER) {
    const t = (valuePct - (100 - TOOLTIP_EDGE_BUFFER)) / TOOLTIP_EDGE_BUFFER;
    tooltipTranslatePct = -50 - 50 * t;
    tooltipEdgePx = TOOLTIP_PARENT_INSET_PX * t;
  }

  // Scope-aware accent: per-person scopes recolor the slider thumb,
  // the floating readout's number, and the tooltip background. Household
  // keeps the default green so it reads as the neutral combined view.
  // Tailwind utility class strings are spelled out statically so the v4
  // generator picks them up at build time.
  const scopeBgClass =
    scope === 'dakota'
      ? 'bg-dakota-soft'
      : scope === 'natalia'
        ? 'bg-natalia-soft'
        : 'bg-green-soft';
  const scopeTextClass =
    scope === 'dakota'
      ? 'text-dakota'
      : scope === 'natalia'
        ? 'text-natalia'
        : 'text-green';
  const sliderVars =
    scope === 'dakota'
      ? {
          '--slider-thumb-color': 'var(--berg-dakota)',
          '--slider-thumb-glow': 'var(--berg-dakota-glow)',
        }
      : scope === 'natalia'
        ? {
            '--slider-thumb-color': 'var(--berg-natalia)',
            '--slider-thumb-glow': 'var(--berg-natalia-glow)',
          }
        : undefined;

  return (
    <div className="py-3 md:py-5">
      {/* Floating readout styled as a tooltip that rides above the
          slider thumb. Inset by 12px on each side to match the thumb's
          center travel range, so left:{pct}% lines up with where the
          handle actually sits. The three words stack vertically inside
          the tooltip body. */}
      <div className="relative h-18 mb-2">
        <div className="absolute inset-x-[12px] inset-y-0">
          <div
            className={clsx(
              'absolute top-0 flex flex-col items-center gap-0.5 whitespace-nowrap rounded-xl px-3 py-2 shadow-xl',
              scopeBgClass,
            )}
            // translateX percentage refers to the tooltip's OWN width,
            // so tying it to valuePct slides the anchor smoothly from
            // the tooltip's left edge (value=min) to its right edge
            // (value=max), staying centered at 50%. Keeps the box
            // within the slider's track at the extremes on narrow
            // screens — a fixed -50% would push half the tooltip past
            // the slider's edge at 75 or 100.
            style={{
              left: `${valuePct}%`,
              transform: `translateX(calc(${tooltipTranslatePct}% + ${tooltipEdgePx}px))`,
            }}
          >
            <span className="hero-eyebrow">Lifespan</span>
            <span
              className={clsx(
                'text-xl font-semibold tabular leading-none',
                scopeTextClass,
              )}
            >
              {value}
            </span>
            <span className="text-fg-tertiary text-xs font-medium leading-none">
              years
            </span>
          </div>
        </div>
      </div>
      <div className="min-w-0">
        {/* h-8 (32px) matches the range-slider utility's height exactly
            so the visible track's top: 50% anchor lines up with where
            the browser draws the native track and thumb. */}
        <div className="relative h-8">
          {/* Active fill — transparent scope-tinted band running from
              the start of the thumb's travel range to the current
              value. Sits behind the hash marks (DOM order: fill first,
              then ticks) so the ticks visually punctuate the fill
              instead of being covered by it. Inset 12px on each side
              to match the thumb travel range; width is the current
              value's pct of that inner range. */}
          <div className="pointer-events-none absolute inset-x-[12px] top-1/2 -translate-y-1/2 h-3.5">
            <div
              className={clsx('absolute left-0 top-0 bottom-0', scopeBgClass)}
              style={{ width: `${valuePct}%` }}
            />
          </div>
          {/* Tick-mark layer in lieu of a visible track. The native
              input's track is transparent in CSS and we don't draw a
              ribbon here either — the hash marks alone communicate the
              scale. Major ticks (every 5 years — 75, 80, 85, 90, 95,
              100) are taller and align with the labels below; minor
              ticks fill in the in-between ages. Mirrors the dial used
              elsewhere in the Retirement page so the two scales feel
              like the same family. Inset 12px on each side to match
              where the thumb's center can travel (thumb is 1.5rem; the
              native track is inset by the same amount in CSS). */}
          <div className="pointer-events-none absolute inset-x-[12px] top-1/2 -translate-y-1/2 h-3.5">
            {Array.from(
              { length: LIFESPAN_MAX - LIFESPAN_MIN + 1 },
              (_, i) => {
                const age = LIFESPAN_MIN + i;
                const isMajor = age % 5 === 0;
                const pct =
                  ((age - LIFESPAN_MIN) /
                    (LIFESPAN_MAX - LIFESPAN_MIN)) *
                  100;
                return (
                  <span
                    key={age}
                    className={clsx(
                      'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-px bg-fg-tertiary',
                      isMajor ? 'h-3.5' : 'h-2',
                    )}
                    style={{ left: `${pct}%` }}
                  />
                );
              },
            )}
          </div>
          <input
            type="range"
            min={LIFESPAN_MIN}
            max={LIFESPAN_MAX}
            step={1}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label="Expected lifespan"
            className="range-slider relative"
            style={sliderVars as CSSProperties}
          />
        </div>
        <div className="mt-0 relative h-3 text-[0.625rem] tabular text-fg-tertiary mx-[12px]">
          {[75, 80, 85, 90, 95, 100].map((age) => {
            const pct =
              ((age - LIFESPAN_MIN) / (LIFESPAN_MAX - LIFESPAN_MIN)) * 100;
            return (
              <span
                key={age}
                className="absolute -translate-x-1/2 whitespace-nowrap"
                style={{ left: `${pct}%` }}
              >
                {age}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SocialSecurityView({
  dakota,
  natalia,
  scope,
  socialSecurity,
}: {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
  socialSecurity: SocialSecurityData;
}) {
  const dakotaFirst = dakota.years[0];
  const nataliaFirst = natalia.years[0];
  const [lifespan, setLifespan] = useState(LIFESPAN_DEFAULT);

  return (
    <div className="flex flex-col gap-4">
      <LifespanControl value={lifespan} onChange={setLifespan} scope={scope} />
      {CLAIM_AGE_OPTIONS.map((age) => (
        <ClaimAgeCard
          key={age}
          claimAge={age}
          scope={scope}
          socialSecurity={socialSecurity}
          dakotaFirst={dakotaFirst}
          nataliaFirst={nataliaFirst}
          lifespan={lifespan}
        />
      ))}
    </div>
  );
}

/* ===== Page ===== */

type View = 'projection' | 'calculator' | 'social-security';

// Builds the sorted list of years available across both projections, plus
// each person's age in that year (from firstYear + offset). Covers the full
// projection (working + retirement years) so the stepper and the chart-click
// handler can navigate to any year on the chart.
function buildYearOptions(
  dakota: RetirementProjection,
  natalia: RetirementProjection
): { year: number; dakotaAge: number | null; nataliaAge: number | null }[] {
  const dFirst = dakota.years[0];
  const nFirst = natalia.years[0];
  const allYears = Array.from(
    new Set([
      ...dakota.years.map((y) => y.year),
      ...natalia.years.map((y) => y.year),
    ])
  ).sort((a, b) => a - b);
  return allYears.map((year) => ({
    year,
    dakotaAge: dFirst ? dFirst.age + (year - dFirst.year) : null,
    nataliaAge: nFirst ? nFirst.age + (year - nFirst.year) : null,
  }));
}

export function RetirementPage() {
  const { data, isLoading, error } = useDashboard();
  const { scope } = useScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const view: View =
    tabParam === 'calculator' || tabParam === 'social-security'
      ? tabParam
      : 'projection';
  const setView = (next: View) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'projection') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const retirement = data?.retirement;
  const dakotaRaw = retirement?.dakota;
  const nataliaRaw = retirement?.natalia;

  // Projection-tab rate overrides. Defaults track the sheet's baked-in
  // assumptions (typically 8% / 4%) so the page renders unchanged out of the
  // box; the user can tweak either knob to explore alternate scenarios. The
  // recomputed projections feed every downstream component (HeroCard, growth
  // chart, AccountMix, YearTable) so all four views stay in lockstep.
  const sheetReturnPct = (dakotaRaw?.assumptions.averageReturnRate ?? 0.08) * 100;
  const sheetWithdrawalPct = (dakotaRaw?.assumptions.withdrawalRule ?? 0.04) * 100;
  const [returnPct, setReturnPct] = useState(sheetReturnPct);
  const [withdrawalPct, setWithdrawalPct] = useState(sheetWithdrawalPct);
  // Once the sheet loads, latch defaults to whatever it actually returned —
  // the initial render runs before data resolves, so the constants above are
  // just safe fallbacks until then.
  const [ratesInitialized, setRatesInitialized] = useState(false);
  if (!ratesInitialized && dakotaRaw) {
    setReturnPct(sheetReturnPct);
    setWithdrawalPct(sheetWithdrawalPct);
    setRatesInitialized(true);
  }

  const dakota = useMemo(
    () =>
      dakotaRaw
        ? recomputeProjection(dakotaRaw, returnPct / 100, withdrawalPct / 100)
        : undefined,
    [dakotaRaw, returnPct, withdrawalPct],
  );
  const natalia = useMemo(
    () =>
      nataliaRaw
        ? recomputeProjection(nataliaRaw, returnPct / 100, withdrawalPct / 100)
        : undefined,
    [nataliaRaw, returnPct, withdrawalPct],
  );

  // Year dropdown options + selection. Default to the projection's last
  // year so a fresh visit shows the long-horizon snapshot most users are
  // there to see. The dropdown only renders on the Projection tab.
  const yearOptions = useMemo(
    () => (dakota && natalia ? buildYearOptions(dakota, natalia) : []),
    [dakota, natalia]
  );
  const lastYearAvailable = yearOptions[yearOptions.length - 1]?.year ?? 0;
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  // Floating projection-controls widget can be collapsed to a single icon
  // button so the user can review the chart/table without it hovering over
  // content. Defaults to collapsed — tap the toggle to expand.
  const [widgetOpen, setWidgetOpen] = useState(false);
  // Once the projection loads, latch the default to its last year. Re-run
  // if the projection ever changes (sheet refresh adds years).
  if (selectedYear == null && lastYearAvailable > 0) {
    setSelectedYear(lastYearAvailable);
  }
  const activeYear = selectedYear ?? lastYearAvailable;
  // Goal labels stack onto two lines on mobile (and abbreviate "expenses" →
  // "exp.") so they don't overlap in the narrow chart.
  const isMobile = useMediaQuery('(max-width: 767.98px)');
  const goalLabel = (n: number) =>
    isMobile ? `Goal ${n / 25}:\n${n}× exp.` : `Goal ${n / 25}: ${n}× expenses`;

  return (
    <div className="page-container space-y-5">
      <header>
        <div className="page-header">
          <div>
            <h1 className="page-title">Retirement</h1>
            <p className="page-subtitle">
              {view === 'projection'
                ? 'Long-term projection from the spreadsheet'
                : view === 'calculator'
                  ? 'Play with the inputs to see a different projection'
                  : 'Estimated benefit at each claim age, applied year by year'}
            </p>
          </div>
        </div>
      </header>
      <div className="tab-row max-md:tab-row-sticky max-md:-mt-5">
        <div className="page-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={view === 'projection'}
            onClick={() => setView('projection')}
            className={clsx('page-tab', view === 'projection' && 'page-tab-active')}
          >
            Projection
          </button>
          <button
            role="tab"
            aria-selected={view === 'calculator'}
            onClick={() => setView('calculator')}
            className={clsx('page-tab', view === 'calculator' && 'page-tab-active')}
          >
            Calculator
          </button>
          <button
            role="tab"
            aria-selected={view === 'social-security'}
            onClick={() => setView('social-security')}
            className={clsx('page-tab', view === 'social-security' && 'page-tab-active')}
          >
            Social Security
          </button>
        </div>
      </div>

      {view === 'projection' && yearOptions.length > 0 && activeYear > 0 && (
        widgetOpen ? (
          <div
            // Sits just below the sticky 60px topbar so the controls are
            // always visible without scrolling. z-40 keeps it under the
            // topbar (z-50) when it visually overlaps during scroll.
            className="fixed z-40 px-3 py-2.5 rounded-[22px] border border-border shadow-xl flex items-end gap-2.5 max-sm:gap-2 top-[calc(60px+env(safe-area-inset-top,0px)+52px)] left-2 right-2 sm:left-auto sm:right-2"
            style={{
              background:
                'color-mix(in srgb, var(--berg-bg-elevated) 60%, transparent)',
              backdropFilter: 'saturate(180%) blur(20px)',
              WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            }}
          >
            <button
              type="button"
              onClick={() => setWidgetOpen(false)}
              aria-label="Hide projection controls"
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full border border-border-strong bg-canvas flex items-center justify-center text-fg-tertiary hover:text-fg hover:border-fg-tertiary transition-colors shadow-md"
            >
              <XIcon size={12} weight="bold" />
            </button>
            <label className="flex flex-col gap-1 sm:w-20 max-sm:flex-1 max-sm:min-w-0">
              <span className="stat-label">Return</span>
              <div className="relative">
                <Select
                  value={Math.round(returnPct)}
                  onChange={(e) => setReturnPct(Number(e.target.value))}
                  className="h-9 py-0"
                  style={{ backgroundImage: 'none' }}
                >
                  {RETURN_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-tertiary tabular text-sm">
                  %
                </span>
              </div>
            </label>
            <label className="flex flex-col gap-1 sm:w-20 max-sm:flex-1 max-sm:min-w-0">
              <span className="stat-label">Withdraw</span>
              <div className="relative">
                <Select
                  value={Math.round(withdrawalPct)}
                  onChange={(e) => setWithdrawalPct(Number(e.target.value))}
                  className="h-9 py-0"
                  style={{ backgroundImage: 'none' }}
                >
                  {WITHDRAWAL_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-tertiary tabular text-sm">
                  %
                </span>
              </div>
            </label>
            <label className="flex flex-col gap-1 sm:w-20 max-sm:flex-1 max-sm:min-w-0">
              <span className="stat-label">Year</span>
              <Select
                value={activeYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="h-9 py-0"
              >
                {yearOptions.map((o) => (
                  <option key={o.year} value={o.year}>
                    {o.year}
                  </option>
                ))}
              </Select>
            </label>
            <button
              type="button"
              onClick={() => {
                setReturnPct(sheetReturnPct);
                setWithdrawalPct(sheetWithdrawalPct);
                setSelectedYear(lastYearAvailable);
              }}
              className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-border bg-overlay hover:bg-subtle hover:border-border-strong transition-colors text-sm text-fg"
              title="Reset to spreadsheet values"
            >
              Reset
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setWidgetOpen(true)}
            aria-label="Show projection controls"
            className="fixed z-40 w-11 h-11 rounded-full border border-border shadow-xl flex items-center justify-center text-fg-secondary hover:text-fg transition-colors top-[calc(60px+env(safe-area-inset-top,0px)+52px)] right-2 sm:right-2"
            style={{
              background:
                'color-mix(in srgb, var(--berg-bg-elevated) 60%, transparent)',
              backdropFilter: 'saturate(180%) blur(20px)',
              WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            }}
          >
            <SlidersHorizontalIcon size={18} weight="bold" />
          </button>
        )
      )}

      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">Loading…</div>
      )}

      {data && dakota && natalia && view === 'projection' && (
        <>
          <div className="grid grid-cols-3 gap-5 max-md:grid-cols-1">
            <div className="col-span-2 max-md:col-span-1">
              <HeroCard
                dakota={dakota}
                natalia={natalia}
                scope={scope}
                selectedYear={activeYear}
                annualExpensesForScope={data.budget.summary.annualExpenses[scope]}
              />
            </div>
            <ContributionsBreakdownCard
              dakota={dakota}
              natalia={natalia}
              scope={scope}
              selectedYear={activeYear}
            />
          </div>
          <RetirementGrowthChart
            dakota={dakota}
            natalia={natalia}
            scope={scope}
            subtitle={formatGrowthSubtitle(dakota, natalia, scope, activeYear)}
            goals={[
              {
                value: data.budget.summary.annualExpenses[scope] * 25,
                label: goalLabel(25),
              },
              {
                value: data.budget.summary.annualExpenses[scope] * 50,
                label: goalLabel(50),
              },
            ]}
            endYear={activeYear}
          />
          <AccountMix
            dakota={dakota}
            natalia={natalia}
            scope={scope}
            selectedYear={activeYear}
          />
          <YearTable
            dakota={dakota}
            natalia={natalia}
            scope={scope}
            selectedYear={activeYear}
            onSelectYear={setSelectedYear}
          />

          <p className="text-xs text-fg-tertiary px-1">
            Active assumptions: {pct(dakota.assumptions.averageReturnRate)} average
            annual return, {pct(dakota.assumptions.withdrawalRule)} withdrawal rule,{' '}
            {pct(dakota.assumptions.hysaApy)} HYSA APY (default:{' '}
            {(sheetReturnPct).toFixed(2)}% return,{' '}
            {(sheetWithdrawalPct).toFixed(2)}% withdrawal).
          </p>
        </>
      )}

      {dakota && natalia && view === 'calculator' && (
        <Calculator dakota={dakota} natalia={natalia} scope={scope} />
      )}

      {data && dakota && natalia && view === 'social-security' && (
        <SocialSecurityView
          dakota={dakota}
          natalia={natalia}
          scope={scope}
          socialSecurity={data.socialSecurity}
        />
      )}
    </div>
  );
}
